import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Star, Check, AlertTriangle, Home } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function ReviewFormPage() {
  const { token }     = useParams();
  const { hotelName } = useSettings();
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo]         = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating]     = useState(0);
  const [hoverRating, setHover] = useState(0);
  const [comment, setComment]   = useState("");
  const [error, setError]       = useState("");

  useEffect(() => {
    fetch(`${API}/review/${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(j => {
        setInfo(j.data);
        setSubmitted(!!j.data.alreadyReviewed);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit() {
    if (!rating) { setError("Please select a star rating."); return; }
    setSubmitting(true); setError("");
    try {
      const res  = await fetch(`${API}/review/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setSubmitted(true);
    } catch (e) { setError(e.message || "Submission failed. Please try again."); }
    finally { setSubmitting(false); }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-10 text-center max-w-sm">
          <AlertTriangle size={36} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Found</h2>
          <p className="text-sm text-gray-500">This review link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-10 text-center max-w-sm w-full">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500
            rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-green-200">
            <Check size={38} className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Thank You!</h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            Your review helps other travellers find {hotelName}. We hope to host you again soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-orange-50/20 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-2.5 justify-center mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500
            flex items-center justify-center shadow-md">
            <Home size={17} className="text-white" />
          </div>
          <p className="font-black text-gray-900 text-sm">{hotelName}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-7 text-center">
          <h1 className="text-xl font-black text-gray-900 mb-1">How was your stay, {info?.guestName}?</h1>
          <p className="text-sm text-gray-400 mb-6">{info?.room?.name ? `${info.room.name} · ` : ""}{info?.bookingRef}</p>

          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} type="button"
                onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                className="transition-transform hover:scale-110">
                <Star size={36} className={(hoverRating || rating) >= n ? "text-amber-500 fill-amber-500" : "text-gray-200"} />
              </button>
            ))}
          </div>

          <textarea value={comment} onChange={e => setComment(e.target.value)}
            rows={4} placeholder="Tell us about your stay (optional)…"
            className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-left
              focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none mb-4" />

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-start gap-2 mb-4 text-left">
              <AlertTriangle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button onClick={submit} disabled={submitting}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white font-bold text-sm hover:from-amber-600 hover:to-orange-600
              shadow-md transition-all disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit Review"}
          </button>
        </div>
      </div>
    </div>
  );
}
